import React from 'react';
import './Segmented.css';

export interface WxbSegmentedOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export interface WxbSegmentedProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  options: WxbSegmentedOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  size?: 'sm' | 'md';
}

const firstEnabledValue = (options: WxbSegmentedOption[]) => (
  options.find((option) => !option.disabled)?.value || ''
);

export const WxbSegmented: React.FC<WxbSegmentedProps> = ({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  className = '',
  size = 'md',
  'aria-label': ariaLabel = '选项切换',
  ...props
}) => {
  const initialValue = options.some((option) => option.value === defaultValue && !option.disabled)
    ? defaultValue!
    : firstEnabledValue(options);
  const [internal, setInternal] = React.useState(initialValue);
  const isControlled = controlledValue !== undefined;
  const requestedValue = isControlled ? controlledValue : internal;
  const current = options.some((option) => option.value === requestedValue && !option.disabled)
    ? requestedValue
    : firstEnabledValue(options);
  const groupRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef(new Map<string, HTMLButtonElement>());

  const updatePill = React.useCallback(() => {
    const group = groupRef.current;
    const activeOption = optionRefs.current.get(current);
    if (!group || !activeOption) return;
    group.style.setProperty('--wxb-segmented-pill-left', `${activeOption.offsetLeft}px`);
    group.style.setProperty('--wxb-segmented-pill-width', `${activeOption.offsetWidth}px`);
  }, [current]);

  React.useEffect(() => {
    updatePill();
    const group = groupRef.current;
    if (!group || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updatePill);
    observer.observe(group);
    return () => observer.disconnect();
  }, [options, updatePill]);

  React.useEffect(() => {
    if (isControlled || internal === current) return;
    setInternal(current);
  }, [current, internal, isControlled]);

  const select = React.useCallback((option: WxbSegmentedOption) => {
    if (option.disabled) return;
    if (!isControlled) setInternal(option.value);
    onChange?.(option.value);
  }, [isControlled, onChange]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, option: WxbSegmentedOption) => {
    const enabledOptions = options.filter((candidate) => !candidate.disabled);
    const currentIndex = enabledOptions.findIndex((candidate) => candidate.value === option.value);
    if (currentIndex < 0 || enabledOptions.length === 0) return;

    let target: WxbSegmentedOption | undefined;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      target = enabledOptions[(currentIndex + 1) % enabledOptions.length];
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      target = enabledOptions[(currentIndex - 1 + enabledOptions.length) % enabledOptions.length];
    }
    if (event.key === 'Home') target = enabledOptions[0];
    if (event.key === 'End') target = enabledOptions[enabledOptions.length - 1];
    if (!target) return;

    event.preventDefault();
    optionRefs.current.get(target.value)?.focus();
    select(target);
  };

  return (
    <div
      {...props}
      className={`wxb-segmented wxb-segmented-${size} ${className}`}
      ref={groupRef}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      <span className="wxb-segmented-pill" aria-hidden="true" />
      {options.map((option) => {
        const selected = current === option.value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              if (node) optionRefs.current.set(option.value, node);
              else optionRefs.current.delete(option.value);
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            disabled={option.disabled}
            className={`wxb-segmented-item ${selected ? 'is-active' : ''} ${option.disabled ? 'is-disabled' : ''}`}
            onClick={() => select(option)}
            onKeyDown={(event) => handleKeyDown(event, option)}
          >
            {option.icon && <span className="wxb-segmented-icon" aria-hidden="true">{option.icon}</span>}
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
